import React, { useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, Snackbar, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsClipboard, BsSearch, BsXLg } from 'react-icons/bs';

import { themeExpert } from '../../theme/expert';

/**
 * Les données qu'un gabarit peut appeler.
 *
 * La liste était un document HTML écrit à la volée dans une fenêtre
 * surgissante : bloquée par la plupart des navigateurs, et impossible à
 * fouiller. Les mêmes données, décrites ici, deviennent cherchables et
 * chaque variable se copie d'un clic.
 */
const SECTIONS = [
  {
    titre: 'Le dossier',
    variables: [
      ['today_date', 'Date du jour'],
      ['reference_interne', 'Référence interne du dossier'],
      ['expertise_nom', "Nom de l'expertise"],
      ['claim_reference_cie', 'Référence sinistre de la compagnie'],
      ['claim_garantees', 'Garanties contractuelles'],
      ['claim_date', 'Date du sinistre'],
      ['claim_adress', 'Adresse du sinistre'],
      ['insurance_contract_reference', 'Référence du contrat'],
      ['insurance_contract_cie', "Nom de l'assureur"],
    ],
  },
  {
    titre: 'Les photos',
    intro: 'Parcourir {photos}, puis pour chaque {photo} :',
    variables: [
      ['photos', 'Liste des photos'],
      ['photo.count', 'Numéro de la photo'],
      ['photo.title', 'Titre'],
      ['photo.description', 'Description'],
      ['photo.category', 'Catégorie'],
      ['photo.group', 'Groupe'],
      ['photo.file', 'Image intégrable'],
    ],
  },
  {
    titre: 'Les intervenants',
    intro: 'Parcourir {personnes}, puis pour chaque {personne} :',
    variables: [
      ['personnes', 'Liste des personnes'],
      ['personne.genre', 'Civilité'],
      ['personne.name', 'Nom'],
      ['personne.firstname', 'Prénom'],
      ['personne.mail', 'Email'],
      ['personne.phone', 'Téléphone'],
      ['personne.role1', 'Rôle principal'],
      ['personne.role2', 'Rôle secondaire'],
      ['personne.reference', 'Référence'],
      ['personne.letterbox', 'Boîte'],
      ['personne.number', 'Numéro'],
      ['personne.street', 'Rue'],
      ['personne.city', 'Ville'],
      ['personne.postcode', 'Code postal'],
      ['personne.country', 'Pays'],
    ],
  },
  {
    titre: 'Le rapport',
    variables: [
      ['report_fields', 'Liste des champs du rapport'],
      ['report_fields.title', 'Titre'],
      ['report_fields.description', 'Description, en texte brut'],
      ['report_fields.at_date', 'Date'],
      ['report_fields.check_box', 'Case cochée — vrai ou faux'],
    ],
  },
  {
    titre: 'Les totaux du chiffrage',
    variables: [
      ['totaux.totalReclamerHtva', 'Total réclamé HTVA'],
      ['totaux.totalCalculerHtva', 'Total calculé HTVA'],
      ['totaux.totalAccorderHtva', 'Total accordé HTVA'],
      ['totaux.totalRecoursHtva', 'Total recours HTVA'],
      ['totaux.totalReclamerTtc', 'Total réclamé TTC'],
      ['totaux.totalCalculerTtc', 'Total calculé TTC'],
      ['totaux.totalAccorderTtc', 'Total accordé TTC'],
      ['totaux.totalRecoursTtc', 'Total recours TTC'],
      ['totaux.posts', 'Liste des postes'],
    ],
  },
  {
    titre: 'Chaque poste de chiffrage',
    intro: 'Parcourir {totaux.posts}, puis pour chaque {post} :',
    variables: [
      ['post.title', 'Titre du poste'],
      ['post.origin_label', "Libellé de l'origine"],
      ['post.tva', 'TVA appliquée'],
      ['post.reclamer', 'Montant réclamé'],
      ['post.accorder', 'Montant accordé'],
      ['post.recours', 'Montant en recours'],
      ['post.total_htva', 'Total HTVA'],
      ['post.total_min', 'Total minimum'],
      ['post.total_max', 'Total maximum'],
      ['post.total_ttc', 'Total TTC'],
      ['post.details', 'Liste des lignes'],
    ],
  },
  {
    titre: 'Chaque ligne de chiffrage',
    intro: 'Parcourir {post.details}, puis pour chaque {detail} :',
    variables: [
      ['detail.title', 'Titre de la ligne'],
      ['detail.quantity', 'Quantité'],
      ['detail.unit', 'Unité'],
      ['detail.unitValue', 'Prix unitaire'],
      ['detail.total', 'Total'],
      ['detail.total_min', 'Total minimum'],
      ['detail.total_max', 'Total maximum'],
      ['detail.typeCosting_label', 'Type de coût'],
    ],
  },
  {
    titre: 'Le contenu',
    intro: 'Parcourir {contenu.items}, puis pour chaque {item} :',
    variables: [
      ['contenu.total_ask', 'Total réclamé'],
      ['contenu.total', 'Total retenu'],
      ['contenu.items', 'Liste des objets'],
      ['item.item', "Nom de l'objet"],
      ['item.description', 'Description'],
      ['item.amount_ask', 'Montant réclamé'],
      ['item.amount_buy', "Valeur d'achat"],
      ['item.date_buy', "Date d'achat"],
      ['item.amount_propose', 'Montant retenu'],
    ],
  },
];

function InfoPopover({ label = 'Données disponibles' }) {
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [copie, setCopie] = useState('');

  const visibles = useMemo(() => {
    const mot = recherche.trim().toLowerCase();
    if (!mot) return SECTIONS;
    return SECTIONS
      .map((section) => ({
        ...section,
        variables: section.variables.filter(
          ([cle, sens]) => cle.toLowerCase().includes(mot)
            || sens.toLowerCase().includes(mot)),
      }))
      .filter((section) => section.variables.length > 0);
  }, [recherche]);

  const copier = (cle) => {
    navigator.clipboard?.writeText(`{${cle}}`)
      .then(() => setCopie(`{${cle}} copié`))
      .catch(() => setCopie("La copie n'a pas abouti."));
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOuvert(true)}>
        {label}
      </Button>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={ouvert} onClose={() => setOuvert(false)}
                fullWidth maxWidth="md" scroll="paper">
          <DialogTitle sx={{ pb: 1 }}>
            Données disponibles
            <Typography variant="caption" color="text.secondary"
                        component="div">
              À écrire entre accolades dans le gabarit. Cliquez sur une
              variable pour la copier.
            </Typography>
          </DialogTitle>

          <Box sx={{ px: 3, pb: 2 }}>
            <TextField
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Chercher une donnée…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><BsSearch /></InputAdornment>
                ),
                endAdornment: recherche ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRecherche('')}
                                aria-label="Effacer">
                      <BsXLg size={12} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          <DialogContent dividers>
            {visibles.length === 0 && (
              <Typography variant="body2" color="text.secondary"
                          sx={{ textAlign: 'center', py: 4 }}>
                Aucune donnée ne correspond.
              </Typography>
            )}

            <Stack spacing={3}>
              {visibles.map((section) => (
                <Box key={section.titre}>
                  <Typography variant="h3">{section.titre}</Typography>
                  {section.intro && (
                    <Typography variant="caption" color="text.secondary"
                                component="div" sx={{ mb: 1 }}>
                      {section.intro}
                    </Typography>
                  )}
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {section.variables.map(([cle, sens]) => (
                      <Stack key={cle} direction="row" spacing={1}
                             alignItems="center">
                        <Tooltip title="Copier">
                          <Box
                            component="button"
                            type="button"
                            onClick={() => copier(cle)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.85rem',
                                  bgcolor: 'background.default', border: '1px solid',
                                  borderColor: 'divider', borderRadius: 1,
                                  px: 1, py: 0.25, cursor: 'pointer',
                                  color: 'primary.main', whiteSpace: 'nowrap',
                                  '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            {`{${cle}}`} <BsClipboard size={10} />
                          </Box>
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary">
                          {sens}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOuvert(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(copie)} autoHideDuration={2000}
                onClose={() => setCopie('')} message={copie}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default InfoPopover;
